/**
 * SummaryStep — Compact booking review
 */

import type { TimeSlot, PlayerInfo } from '../types'
import type { MockPriceBreakdown } from '../mock-data'
import { formatPrice } from '../mock-data'

interface SummaryStepProps {
  clubName: string
  selectedDate: Date | null
  selectedTimeSlot: TimeSlot | null
  players: PlayerInfo[]
  priceBreakdown: MockPriceBreakdown | null
  timezone?: string
}

export function SummaryStep({ clubName, selectedDate, selectedTimeSlot, players, priceBreakdown, timezone }: SummaryStepProps) {
  const tz = timezone || 'Australia/Melbourne'

  const formatDate = (date: Date): string => {
    try {
      return new Intl.DateTimeFormat('en-AU', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        timeZone: tz,
      }).format(date)
    } catch {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    }
  }

  const formatTime = (isoString: string): string => {
    try {
      const date = new Date(isoString)
      return new Intl.DateTimeFormat('en-AU', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: tz,
      }).format(date)
    } catch {
      const date = new Date(isoString)
      const hours = date.getHours()
      const minutes = date.getMinutes()
      const ampm = hours >= 12 ? 'PM' : 'AM'
      return `${hours % 12 || 12}:${minutes.toString().padStart(2, '0')} ${ampm}`
    }
  }

  return (
    <div>
      <div className="bw2-section-label">Review Booking</div>

      <div className="bw2-summary" role="region" aria-label="Booking summary">
        {/* Booking Details */}
        <div className="bw2-summary__section" role="group" aria-label="Booking details">
          <div className="bw2-summary__section-title">Details</div>
          <div className="bw2-summary__row">
            <span className="bw2-summary__label">Club</span>
            <span className="bw2-summary__value">{clubName}</span>
          </div>
          {selectedTimeSlot?.courseName && (
            <div className="bw2-summary__row">
              <span className="bw2-summary__label">Course</span>
              <span className="bw2-summary__value">{selectedTimeSlot.courseName}</span>
            </div>
          )}
          {selectedDate && (
            <div className="bw2-summary__row">
              <span className="bw2-summary__label">Date</span>
              <span className="bw2-summary__value">{formatDate(selectedDate)}</span>
            </div>
          )}
          {selectedTimeSlot && (
            <div className="bw2-summary__row">
              <span className="bw2-summary__label">Tee Time</span>
              <span className="bw2-summary__value">{formatTime(selectedTimeSlot.time)}</span>
            </div>
          )}
          <div className="bw2-summary__row">
            <span className="bw2-summary__label">Players</span>
            <span className="bw2-summary__value">{players.length}</span>
          </div>
        </div>

        {/* Players */}
        <div className="bw2-summary__section" role="group" aria-label="Players">
          <div className="bw2-summary__section-title">Players</div>
          {players.map((player) => (
            <div key={player.id} className="bw2-summary__row">
              <span className="bw2-summary__label">
                {player.firstName} {player.lastName}
                {player.isPrimary && <span className="bw2-badge bw2-badge--info" style={{ marginLeft: '4px' }}>Primary</span>}
              </span>
              <span className="bw2-summary__value">{player.priceLabel || '—'}</span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        {priceBreakdown && (
          <div className="bw2-summary__section" role="group" aria-label="Pricing breakdown">
            <div className="bw2-summary__section-title">Pricing</div>
            {priceBreakdown.players.map((p, i) => (
              <div key={i} className="bw2-summary__row">
                <span className="bw2-summary__label">{p.name} ({p.label})</span>
                <span className="bw2-summary__value">{formatPrice(p.priceCents, priceBreakdown.currencySymbol)}</span>
              </div>
            ))}
            <div className="bw2-divider" aria-hidden="true" />
            <div className="bw2-summary__row">
              <span className="bw2-summary__label">Subtotal</span>
              <span className="bw2-summary__value">{formatPrice(priceBreakdown.subtotalCents, priceBreakdown.currencySymbol)}</span>
            </div>
            <div className="bw2-summary__row">
              <span className="bw2-summary__label bw2-summary__label--with-tooltip">
                Service Fee (4%)
                <span className="bw2-summary__tooltip-wrap">
                  <span className="bw2-summary__tooltip-icon" aria-label="Service fee info">ⓘ</span>
                  <span className="bw2-summary__tooltip-text">
                    This fee covers the booking technology platform and ongoing support for your reservation.
                  </span>
                </span>
              </span>
              <span className="bw2-summary__value">{formatPrice(priceBreakdown.serviceFeeCents, priceBreakdown.currencySymbol)}</span>
            </div>
            <div className="bw2-summary__row">
              <span className="bw2-summary__label bw2-summary__label--with-tooltip">
                Processing Fee
                <span className="bw2-summary__tooltip-wrap">
                  <span className="bw2-summary__tooltip-icon" aria-label="Processing fee info">ⓘ</span>
                  <span className="bw2-summary__tooltip-text">
                    Stripe payment processing fee (1.75% + 30¢ per transaction).
                  </span>
                </span>
              </span>
              <span className="bw2-summary__value">{formatPrice(priceBreakdown.processingFeeCents, priceBreakdown.currencySymbol)}</span>
            </div>
            <div className="bw2-divider" aria-hidden="true" />
            <div className="bw2-summary__row" aria-label={`Total: ${formatPrice(priceBreakdown.totalCents, priceBreakdown.currencySymbol)}`}>
              <span className="bw2-summary__label" style={{ fontWeight: 600, color: 'var(--bw2-text)' }}>Total</span>
              <span className="bw2-summary__total">{formatPrice(priceBreakdown.totalCents, priceBreakdown.currencySymbol)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
